import { LineChart } from "@mui/x-charts/LineChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { OvalLoader } from "../../../components/common/Loader";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

const ChartSwiper = ({ data, ovalLoading, hasPermission }) => {
  // Chart data derived from API response
  const chartDataSet = data.monthWiseBusinessList || [];
  const chartDataSet1 = data.monthWise121RFlVisitorList || [];
  const chartDataSet2 = data.monthWiseIncomesExpensesList || [];
  const chartDataSet3 = (data.chapterWiseBusiness || [])
    .filter((item) => item.amount > 0)
    .map((item, index) => ({
      id: index,
      value: item.amount,
      label: item.chapter_name,
    }));

  const chartDataSet4 = (data.chapterWiseOneToOne || [])
    .filter((item) => item.count > 0)
    .map((item, index) => ({
      id: index,
      value: item.count,
      label: item.chapter_name,
    }));

  const chartDataSet5 = (data.chapterWiseReferrals || [])
    .filter((item) => item.count > 0)
    .map((item, index) => ({
      id: index,
      value: item.count,
      label: item.chapter_name,
    }));

  const chartDataSet6 = (data.chapterWiseVisitors || [])
    .filter((item) => item.count > 0)
    .map((item, index) => ({
      id: index,
      value: item.count,
      label: item.chapter_name,
    }));

  // Totals for arc labels
  const totalBiz = chartDataSet3.reduce((sum, item) => sum + item.value, 0);
  const totalFBM = chartDataSet4.reduce((sum, item) => sum + item.value, 0);
  const totalRef = chartDataSet5.reduce((sum, item) => sum + item.value, 0);
  const totalVis = chartDataSet6.reduce((sum, item) => sum + item.value, 0);

  // Custom rich green color palette using opacity
  const greenThemeColors = [
    "rgba(26, 67, 49, 1)",
    "rgba(26, 67, 49, 0.85)",
    "rgba(26, 67, 49, 0.7)",
    "rgba(26, 67, 49, 0.55)",
    "rgba(26, 67, 49, 0.4)",
    "rgba(26, 67, 49, 0.25)",
  ];

  // Common chart styling
  const chartHeight = 320;

  // Value formatters
  const currencyFormatter = (val) => {
    const value = val?.value !== undefined ? val.value : val;
    if (value == null) return "";
    return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value)}`;
  };

  const yAxisFormatter = (val) => {
    const value = val?.value !== undefined ? val.value : val;
    if (value == null) return "";
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
      value,
    );
  };

  if (!hasPermission("dashboard-tyns-data-view")) {
    return null;
  }

  return (
    <div className="chart-section">
      {/* Business Achieved - Full Width Area Chart */}
      <div className="chart-card">
        <div className="chart-card-header">
          <div className="chart-card-title-group">
            <div
              className="chart-card-icon"
              style={{ background: "#e8f5e9", color: "#1A4331" }}
            >
              <i className="bx bx-dollar-circle"></i>
            </div>
            <div>
              <h3 className="chart-card-title">Overall Business Achieved</h3>
              <p className="chart-card-subtitle">
                {ovalLoading ? (
                  <OvalLoader color="#1A4331" />
                ) : (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip className="tyntooltip">
                        {data.overallTynsAmountText || ""}
                      </Tooltip>
                    }
                  >
                    <span className="chart-card-amount">
                      {data.overallTynsAmount}
                    </span>
                  </OverlayTrigger>
                )}
              </p>
            </div>
          </div>
        </div>
        <div className="chart-card-body">
          {ovalLoading ? (
            <div className="chart-loader">
              <OvalLoader color="#1A4331" />
            </div>
          ) : chartDataSet.length > 0 ? (
            <LineChart
              dataset={chartDataSet}
              xAxis={[
                {
                  scaleType: "band",
                  dataKey: "month",
                  tickPlacement: "middle",
                  tickLabelPlacement: "middle",
                },
              ]}
              yAxis={[
                {
                  valueFormatter: yAxisFormatter,
                },
              ]}
              series={[
                {
                  dataKey: "amount",
                  // label: "Business",
                  valueFormatter: currencyFormatter,
                  color: "#1A4331",
                  area: true,
                  showMark: true,
                  curve: "monotoneX",
                },
              ]}
              height={chartHeight}
              margin={{ top: 20, right: 20, bottom: 30, left: 70 }}
              grid={{ vertical: true, horizontal: true }}
              sx={{
                "& .MuiAreaElement-root": {
                  fill: "rgba(26, 67, 49, 0.12)",
                },
                "& .MuiLineElement-root": {
                  strokeWidth: 2.5,
                },
                "& .MuiMarkElement-root": {
                  strokeWidth: 2,
                  scale: "0.8",
                  fill: "#fff",
                },
              }}
            />
          ) : (
            <div className="chart-empty">No data available</div>
          )}
        </div>
      </div>

      {/* FBMs, RFLs, TYNs, Visitors - Full Width Bar Chart */}
      <div className="chart-card">
        <div className="chart-card-header">
          <div className="chart-card-title-group">
            <div
              className="chart-card-icon"
              style={{ background: "#e3f2fd", color: "#1976d2" }}
            >
              <i className="bx bx-bar-chart-alt-2"></i>
            </div>
            <div>
              <h3 className="chart-card-title">Activities Overview</h3>
              <p className="chart-card-subtitle-text">
                FBMs, RFLs, TYNs & Visitors month-wise breakdown
              </p>
            </div>
          </div>
          <div className="chart-legend-indicators">
            <span className="chart-legend-item">
              <span
                className="chart-legend-dot"
                style={{ background: "#61A375" }}
              ></span>
              TYNs
            </span>
            <span className="chart-legend-item">
              <span
                className="chart-legend-dot"
                style={{ background: "#4A8F9B" }}
              ></span>
              FBMs
            </span>
            <span className="chart-legend-item">
              <span
                className="chart-legend-dot"
                style={{ background: "#D97D54" }}
              ></span>
              RFLs
            </span>
            <span className="chart-legend-item">
              <span
                className="chart-legend-dot"
                style={{ background: "#6B5C7B" }}
              ></span>
              Visitors
            </span>
          </div>
        </div>
        <div className="chart-card-body">
          {ovalLoading ? (
            <div className="chart-loader">
              <OvalLoader color="#1976d2" />
            </div>
          ) : chartDataSet1.length > 0 ? (
            <BarChart
              dataset={chartDataSet1}
              xAxis={[
                {
                  dataKey: "month",
                  scaleType: "band",
                  categoryGapRatio: 0.2,
                  barGapRatio: 0.05,
                },
              ]}
              yAxis={[
                {
                  valueFormatter: yAxisFormatter,
                },
              ]}
              slotProps={{ legend: { hidden: true } }}
              series={[
                {
                  dataKey: "tyfcb",
                  label: "TYNs",
                  color: "#61A375",
                },
                {
                  dataKey: "one_to_one",
                  label: "FBMs",
                  color: "#4A8F9B",
                },
                {
                  dataKey: "referral",
                  label: "RFLs",
                  color: "#D97D54",
                },
                {
                  dataKey: "visitor",
                  label: "Visitors",
                  color: "#6B5C7B",
                },
              ]}
              height={chartHeight}
              margin={{ top: 20, right: 20, bottom: 30, left: 50 }}
              grid={{ vertical: true, horizontal: true }}
              sx={{
                "& .MuiBarElement-root": {
                  rx: 4,
                },
              }}
            />
          ) : (
            <div className="chart-empty">No data available</div>
          )}
        </div>
      </div>

      {/* Income vs Expense - Full Width Area Chart */}
      <div className="chart-card">
        <div className="chart-card-header">
          <div className="chart-card-title-group">
            <div
              className="chart-card-icon"
              style={{ background: "#fce4ec", color: "#e53935" }}
            >
              <i className="bx bx-trending-up"></i>
            </div>
            <div>
              <h3 className="chart-card-title">Income vs Expense</h3>
              <p className="chart-card-subtitle-text">
                Monthly income and expense comparison
              </p>
            </div>
          </div>
          <div className="chart-legend-indicators">
            <span className="chart-legend-item">
              <span
                className="chart-legend-dot"
                style={{ background: "#149614" }}
              ></span>
              Income
            </span>
            <span className="chart-legend-item">
              <span
                className="chart-legend-dot"
                style={{ background: "#FF3E1D" }}
              ></span>
              Expense
            </span>
          </div>
        </div>
        <div className="chart-card-body">
          {ovalLoading ? (
            <div className="chart-loader">
              <OvalLoader color="#e53935" />
            </div>
          ) : chartDataSet2.length > 0 ? (
            <LineChart
              dataset={chartDataSet2}
              xAxis={[{ dataKey: "month", scaleType: "band" }]}
              yAxis={[
                {
                  valueFormatter: yAxisFormatter,
                },
              ]}
              slotProps={{ legend: { hidden: true } }}
              series={[
                {
                  id: "income",
                  dataKey: "income",
                  label: "Income",
                  valueFormatter: currencyFormatter,
                  color: "#149614",
                  area: true,
                  showMark: true,
                  curve: "monotoneX",
                },
                {
                  id: "expense",
                  dataKey: "expense",
                  label: "Expense",
                  valueFormatter: currencyFormatter,
                  color: "#FF3E1D",
                  area: true,
                  showMark: true,
                  curve: "monotoneX",
                },
              ]}
              height={chartHeight}
              margin={{ top: 20, right: 20, bottom: 30, left: 70 }}
              grid={{ vertical: true, horizontal: true }}
              sx={{
                "& .MuiAreaElement-series-income": {
                  fill: "rgba(20, 150, 20, 0.12)",
                },
                "& .MuiAreaElement-series-expense": {
                  fill: "rgba(255, 62, 29, 0.1)",
                },
                "& .MuiLineElement-series-income": {
                  strokeWidth: 2.5,
                },
                "& .MuiLineElement-series-expense": {
                  strokeWidth: 2.5,
                },
                "& .MuiMarkElement-root": {
                  strokeWidth: 2,
                  scale: "0.8",
                  fill: "#fff",
                },
              }}
            />
          ) : (
            <div className="chart-empty">No data available</div>
          )}
        </div>
      </div>

      <div className="pie-charts-grid">
        {/* Chapter Wise Business - Pie Chart */}
        <div className="chart-card">
          <div className="chart-card-header">
            <div className="chart-card-title-group">
              <div
                className="chart-card-icon"
                style={{ background: "#fef3c7", color: "#d97706" }}
              >
                <i className="bx bx-pie-chart-alt-2"></i>
              </div>
              <div>
                <h3 className="chart-card-title">Chapter Wise Business</h3>
                <p className="chart-card-subtitle-text">
                  Business generated by each chapter
                </p>
              </div>
            </div>
          </div>
          <div className="chart-card-body">
            {ovalLoading ? (
              <div className="chart-loader">
                <OvalLoader color="#d97706" />
              </div>
            ) : chartDataSet3.length > 0 ? (
              <PieChart
                colors={greenThemeColors}
                series={[
                  {
                    data: chartDataSet3,
                    innerRadius: 0,
                    outerRadius: 100,
                    paddingAngle: 2,
                    cornerRadius: 4,
                    highlightScope: { faded: "global", highlighted: "item" },
                    faded: {
                      innerRadius: 0,
                      additionalRadius: -30,
                      color: "gray",
                    },
                    valueFormatter: currencyFormatter,
                    arcLabel: (item) => {
                      const pct =
                        totalBiz > 0
                          ? ((item.value / totalBiz) * 100).toFixed(0)
                          : 0;
                      return pct >= 5
                        ? `${pct}%`
                        : "";
                    },
                    arcLabelMinAngle: 20,
                  },
                ]}
                height={chartHeight}
                margin={{ top: 20, right: 120, bottom: 20, left: 10 }}
                sx={{
                  "& .MuiPieArcLabel-root": {
                    fill: "#fff",
                    fontWeight: 600,
                    fontSize: "13px",
                  },
                }}
                slotProps={{
                  legend: {
                    direction: "column",
                    position: { vertical: "middle", horizontal: "right" },
                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    markGap: 5,
                    itemGap: 10,
                  },
                }}
              />
            ) : (
              <div className="chart-empty">No data available</div>
            )}
          </div>
        </div>

        {/* Chapter Wise FBMs - Pie Chart */}
        <div className="chart-card">
          <div className="chart-card-header">
            <div className="chart-card-title-group">
              <div
                className="chart-card-icon"
                style={{ background: "#e0f2fe", color: "#0284c7" }}
              >
                <i className="bx bx-conversation"></i>
              </div>
              <div>
                <h3 className="chart-card-title">Chapter Wise FBMs</h3>
                <p className="chart-card-subtitle-text">
                  FBMs completed by each chapter
                </p>
              </div>
            </div>
          </div>
          <div className="chart-card-body">
            {ovalLoading ? (
              <div className="chart-loader">
                <OvalLoader color="#0284c7" />
              </div>
            ) : chartDataSet4.length > 0 ? (
              <PieChart
                colors={greenThemeColors}
                series={[
                  {
                    data: chartDataSet4,
                    innerRadius: 0,
                    outerRadius: 100,
                    paddingAngle: 2,
                    cornerRadius: 4,
                    highlightScope: { faded: "global", highlighted: "item" },
                    faded: {
                      innerRadius: 0,
                      additionalRadius: -30,
                      color: "gray",
                    },
                    valueFormatter: yAxisFormatter,
                    arcLabel: (item) => {
                      const pct =
                        totalFBM > 0
                          ? ((item.value / totalFBM) * 100).toFixed(0)
                          : 0;
                      return pct >= 5
                        ? `${pct}%`
                        : "";
                    },
                    arcLabelMinAngle: 20,
                  },
                ]}
                height={chartHeight}
                margin={{ top: 20, right: 120, bottom: 20, left: 10 }}
                sx={{
                  "& .MuiPieArcLabel-root": {
                    fill: "#fff",
                    fontWeight: 600,
                    fontSize: "13px",
                  },
                }}
                slotProps={{
                  legend: {
                    direction: "column",
                    position: { vertical: "middle", horizontal: "right" },
                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    markGap: 5,
                    itemGap: 10,
                  },
                }}
              />
            ) : (
              <div className="chart-empty">No data available</div>
            )}
          </div>
        </div>

        {/* Chapter Wise Referrals - Pie Chart */}
        <div className="chart-card">
          <div className="chart-card-header">
            <div className="chart-card-title-group">
              <div
                className="chart-card-icon"
                style={{ background: "#ffedd5", color: "#ea580c" }}
              >
                <i className="bx bx-share-alt"></i>
              </div>
              <div>
                <h3 className="chart-card-title">Chapter Wise Referrals</h3>
                <p className="chart-card-subtitle-text">
                  Referrals given by each chapter
                </p>
              </div>
            </div>
          </div>
          <div className="chart-card-body">
            {ovalLoading ? (
              <div className="chart-loader">
                <OvalLoader color="#ea580c" />
              </div>
            ) : chartDataSet5.length > 0 ? (
              <PieChart
                colors={greenThemeColors}
                series={[
                  {
                    data: chartDataSet5,
                    innerRadius: 0,
                    outerRadius: 100,
                    paddingAngle: 2,
                    cornerRadius: 4,
                    highlightScope: { faded: "global", highlighted: "item" },
                    faded: {
                      innerRadius: 0,
                      additionalRadius: -30,
                      color: "gray",
                    },
                    valueFormatter: yAxisFormatter,
                    arcLabel: (item) => {
                      const pct =
                        totalRef > 0
                          ? ((item.value / totalRef) * 100).toFixed(0)
                          : 0;
                      return pct >= 5
                        ? `${pct}%`
                        : "";
                    },
                    arcLabelMinAngle: 20,
                  },
                ]}
                height={chartHeight}
                margin={{ top: 20, right: 120, bottom: 20, left: 10 }}
                sx={{
                  "& .MuiPieArcLabel-root": {
                    fill: "#fff",
                    fontWeight: 600,
                    fontSize: "13px",
                  },
                }}
                slotProps={{
                  legend: {
                    direction: "column",
                    position: { vertical: "middle", horizontal: "right" },
                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    markGap: 5,
                    itemGap: 10,
                  },
                }}
              />
            ) : (
              <div className="chart-empty">No data available</div>
            )}
          </div>
        </div>

        {/* Chapter Wise Visitors - Pie Chart */}
        <div className="chart-card">
          <div className="chart-card-header">
            <div className="chart-card-title-group">
              <div
                className="chart-card-icon"
                style={{ background: "#f3e8ff", color: "#9333ea" }}
              >
                <i className="bx bx-group"></i>
              </div>
              <div>
                <h3 className="chart-card-title">Chapter Wise Visitors</h3>
                <p className="chart-card-subtitle-text">
                  Visitors brought by each chapter
                </p>
              </div>
            </div>
          </div>
          <div className="chart-card-body">
            {ovalLoading ? (
              <div className="chart-loader">
                <OvalLoader color="#9333ea" />
              </div>
            ) : chartDataSet6.length > 0 ? (
              <PieChart
                colors={greenThemeColors}
                series={[
                  {
                    data: chartDataSet6,
                    innerRadius: 0,
                    outerRadius: 100,
                    paddingAngle: 2,
                    cornerRadius: 4,
                    highlightScope: { faded: "global", highlighted: "item" },
                    faded: {
                      innerRadius: 0,
                      additionalRadius: -30,
                      color: "gray",
                    },
                    valueFormatter: yAxisFormatter,
                    arcLabel: (item) => {
                      const pct =
                        totalVis > 0
                          ? ((item.value / totalVis) * 100).toFixed(0)
                          : 0;
                      return pct >= 5
                        ? `${pct}%`
                        : "";
                    },
                    arcLabelMinAngle: 20,
                  },
                ]}
                height={chartHeight}
                margin={{ top: 20, right: 120, bottom: 20, left: 10 }}
                sx={{
                  "& .MuiPieArcLabel-root": {
                    fill: "#fff",
                    fontWeight: 600,
                    fontSize: "13px",
                  },
                }}
                slotProps={{
                  legend: {
                    direction: "column",
                    position: { vertical: "middle", horizontal: "right" },
                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    markGap: 5,
                    itemGap: 10,
                  },
                }}
              />
            ) : (
              <div className="chart-empty">No data available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartSwiper;
